import React, { useMemo, useCallback, type MouseEvent, type KeyboardEvent } from 'react';
import { MenuGroup, Section } from '@atlaskit/menu';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { type IssueId, toIssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import type { ListItemMenuOptionProps } from '@atlassian/jira-software-roadmap-timeline-table/src/renderers/index.tsx';
import { useIssuesMap } from '../../../../common/utils/use-issues-map.tsx';
import type { OnJiraItemUpdate } from '../../../../controllers/update-item/types.tsx';
import {
	REMOVE_DUE_DATE,
	REMOVE_START_DATE,
	REMOVE_START_DATE_AND_DUE_DATE,
} from '../../constants.tsx';
import messages from './messages.tsx';

type Props = {
	id: IssueId;
	Option: ListItemMenuOptionProps['Option'];
	setCloseMenu: ListItemMenuOptionProps['setCloseMenu'];
	onItemUpdate: OnJiraItemUpdate;
};

const RemoveDatesOption = ({ id, Option, setCloseMenu, onItemUpdate }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [{ issuesMap }] = useIssuesMap();
	const issue = issuesMap.get(id);

	const isStartDateClearable = Boolean(issue?.startDate);
	const isDueDateClearable = Boolean(issue?.dueDate);

	const handleOnClick = useCallback(
		(isStartDateCleared: boolean, isDueDateCleared: boolean) =>
			(event: MouseEvent | KeyboardEvent) => {
				const analyticsEvent = createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'option',
				});
				event.preventDefault();
				fireUIAnalytics(analyticsEvent, 'removeDatesOption', {
					isStartDateCleared,
					isDueDateCleared,
				});
				onItemUpdate(
					toIssueId(id),
					{
						dates: {
							...(isStartDateCleared && { startDate: { date: null } }),
							...(isDueDateCleared && { dueDate: { date: null } }),
						},
						meta: {
							isDragged: false,
						},
					},
					analyticsEvent,
				);
				setCloseMenu(event);
			},
		[createAnalyticsEvent, id, onItemUpdate, setCloseMenu],
	);

	const options = useMemo(
		() => [
			{
				optionId: REMOVE_START_DATE,
				isEnabled: isStartDateClearable,
				label: formatMessage(messages.removeStartDate),
				onClick: handleOnClick(true, false),
			},
			{
				optionId: REMOVE_DUE_DATE,
				isEnabled: isDueDateClearable,
				label: formatMessage(messages.removeDueDate),
				onClick: handleOnClick(false, true),
			},
			{
				optionId: REMOVE_START_DATE_AND_DUE_DATE,
				isEnabled: isStartDateClearable && isDueDateClearable,
				label: formatMessage(messages.removeBothDates),
				onClick: handleOnClick(true, true),
			},
		],
		[formatMessage, handleOnClick, isDueDateClearable, isStartDateClearable],
	);

	const initialFocusIndex = useMemo(
		() => options.findIndex(({ isEnabled }) => isEnabled === true),
		[options],
	);

	return (
		<Option
			label={formatMessage(messages.removeDates)}
			isDisabled={!isStartDateClearable && !isDueDateClearable}
		>
			{({ setInitialFocusRef }) => (
				<MenuGroup>
					<Section>
						{options.map(({ optionId, isEnabled, ...optionProps }, index) => (
							<Option
								key={optionId}
								isDisabled={!isEnabled}
								setInitialFocusRef={index === initialFocusIndex ? setInitialFocusRef : undefined}
								{...optionProps}
							/>
						))}
					</Section>
				</MenuGroup>
			)}
		</Option>
	);
};

export default RemoveDatesOption;
